"use client"

import { Tooltip, TooltipTrigger, TooltipContent, cn } from "visuais"
import { CheckCircle2, Zap } from "lucide-react"
import { useState, useEffect } from "react"

interface ProgressBarProps {
  progress: number
  className?: string
  showPercentage?: boolean
  pendingItems?: string[]
  variant?: "default" | "success" | "pulse"
  size?: "sm" | "md" | "lg"
  animated?: boolean
}

export function ProgressBar({
  progress,
  className,
  showPercentage = true,
  pendingItems = [],
  variant = "default",
  size = "md",
  animated = true,
}: ProgressBarProps) {
  const clampedProgress = Math.min(Math.max(progress, 0), 100)
  const isComplete = clampedProgress >= 100
  const [displayProgress, setDisplayProgress] = useState(0)

  useEffect(() => {
    if (animated) {
      const timer = setTimeout(() => {
        setDisplayProgress(clampedProgress)
      }, 100)
      return () => clearTimeout(timer)
    } else {
      setDisplayProgress(clampedProgress)
    }
  }, [clampedProgress, animated])

  const sizeClasses = {
    sm: "h-2",
    md: "h-3",
    lg: "h-4",
  }

  const getProgressBarContent = () => {
    if (isComplete) {
      return (
        <div
          className="h-full rounded-full bg-green-500 transition-all duration-700 ease-out relative overflow-hidden"
          style={{ width: `${displayProgress}%` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-green-400 via-green-500 to-green-600" />
          <div
            className="absolute inset-0 animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/40 to-transparent"
            style={{ backgroundSize: "200% 100%" }}
          />
          {/* borda brilhante girando */}
          <div className="absolute -inset-0.5 rounded-full border-2 border-green-400 animate-[spin_3s_linear_infinite]" />
          <CheckCircle2 className="absolute top-1/2 left-1/2 size-3 text-white -translate-x-1/2 -translate-y-1/2" />
        </div>
      )
    }

    return (
      <div
        className={cn(
          "h-full rounded-full transition-all duration-500 ease-out relative overflow-hidden",
          variant === "pulse" && animated && "animate-pulse",
        )}
        style={{ width: `${displayProgress}%` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-cyan-500 to-sky-500" />
        {animated && (
          <>
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_2s_infinite]"
              style={{ backgroundSize: "200% 100%" }}
            />
            <div className="absolute top-0 right-0 w-6 h-full">
              <div className="w-full h-full bg-gradient-to-l from-cyan-400/80 to-transparent rounded-r-full animate-pulse" />
              {variant === "pulse" && (
                <Zap className="absolute top-1/2 right-1 size-3 text-white animate-bounce -translate-y-1/3" />
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={cn("w-full cursor-pointer relative group", className)}>
          <div
            className={cn(
              "w-full bg-gray-200 dark:bg-gray-500 rounded-full overflow-hidden relative shadow-inner transition-all duration-300 group-hover:shadow-lg",
              sizeClasses[size],
            )}
          >
            {getProgressBarContent()}
          </div>
          {showPercentage && (
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium text-gray-800 dark:text-gray-200 shadow-md border border-gray-200 dark:border-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {Math.round(displayProgress)}%
            </div>
          )}
        </div>
      </TooltipTrigger>

      {pendingItems.length > 0 && (
        <TooltipContent
          side="top"
          className="max-w-xs bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 shadow-lg p-3 rounded-lg"
        >
          <ul className="space-y-1">
            {pendingItems.map((item, i) => (
              <li
                key={i}
                className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2 transition-transform duration-200 hover:translate-x-1 hover:text-gray-500 dark:hover:text-gray-400"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 mt-2 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </TooltipContent>
      )}
    </Tooltip>
  )
}
